import { useCallback } from 'react';

import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import {
  RegistrationAddressT,
} from '@consumer-frontend/nc-utils';
import { Fieldset } from '@consumer-frontend/shared-ui';

import { NcAddressFields } from '../NcAddressForm/NcAddressFields';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcPhoneInput } from '../NcPhoneInput';
import { phoneNumberValidations } from '../NcPhoneNumberPage';
import { NcPhoneNumberDisclaimer } from '../NcPhoneNumberPage/NcPhoneNumberDisclaimer';

type NcContactInfoReactFormT = RegistrationAddressT & {
  phoneNumber: string;
};

type NcContactInfoRegDataT = {
  address: RegistrationAddressT;
  phoneNumber?: string;
};

type NcVerifyContactInfoFormPropsT = {
  defaultAddress: RegistrationAddressT;
  onSubmit: (data: NcContactInfoRegDataT) => Promise<void>;
  className?: string;
  defaultPhoneNumber?: string;
  onBack?: () => void;
};

export const NcVerifyContactInfoForm = ({
  defaultAddress,
  defaultPhoneNumber = '',
  onSubmit,
}: NcVerifyContactInfoFormPropsT): React.JSX.Element => {
  const methods = useForm<NcContactInfoReactFormT>({
    defaultValues: {
      ...defaultAddress,
      phoneNumber: defaultPhoneNumber,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const handleDataSubmit = useCallback<SubmitHandler<NcContactInfoReactFormT>>(
    async (formData) => {
      await onSubmit({
        address: {
          addressLine1: formData.addressLine1.trim(),
          addressLine2: formData.addressLine2?.trim(),
          city: formData.city.trim(),
          postalCode: formData.postalCode.trim(),
          state: formData.state,
        },
        phoneNumber: formData.phoneNumber.trim(),
      });
    },
    [onSubmit],
  );

  return (
    <FormProvider {...methods}>
      <NcForm
        className="d-flex flex-column gap-4"
        onSubmit={handleSubmit(handleDataSubmit)}
      >
        <Controller
          control={control}
          name="phoneNumber"
          rules={phoneNumberValidations}
          render={({ field }) => (
            <NcPhoneInput
              {...field}
              autoComplete="tel-national"
              errorMessage={errors.phoneNumber?.message}
              id="phoneNumber"
              label="Phone Number"
            />
          )}
        />
        <NcAddressFields />

        <Fieldset className="cta-buttons">
          <NcButton
            loading={isSubmitting}
            type="submit"
            variant="primary"
          >
            Continue
          </NcButton>
        </Fieldset>

        <NcPhoneNumberDisclaimer />
      </NcForm>
    </FormProvider>
  );
};
